import { ref, computed, watch } from 'vue'
import { MONTHS_ID, DAY_NAMES } from '../data/constants'

const selectedDate = ref('')
const expandedMonths = ref({})
const searchQuery = ref('')
const _availableDates = ref([]) // dates from DB
const _datesWithAnalysis = ref(new Set()) // dates with completed analysis

// Build timeline from available dates in the database
const timeline = computed(() => {
  const dates = _availableDates.value
  if (!dates || dates.length === 0) return []

  const data = {}
  // Sort descending (newest first)
  const sorted = [...dates].sort().reverse()

  for (const dateStr of sorted) {
    const d = new Date(dateStr + 'T00:00:00')
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    if (!data[key]) {
      data[key] = {
        label: `${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}`,
        days: [],
        key,
      }
    }
    data[key].days.push({
      date: dateStr,
      label: `${DAY_NAMES[d.getDay()]}, ${d.getDate()}`,
      isToday: dateStr === new Date().toISOString().slice(0, 10),
      hasAnalysis: _datesWithAnalysis.value.has(dateStr),
    })
  }
  return Object.values(data)
})

const filteredTimeline = computed(() => {
  const q = searchQuery.value.toLowerCase().trim()
  if (!q) return timeline.value
  return timeline.value
    .map((m) => {
      if (m.label.toLowerCase().includes(q) || m.key.includes(q)) return m
      const fd = m.days.filter(
        (d) => d.date.includes(q) || d.label.toLowerCase().includes(q)
      )
      return fd.length ? { ...m, days: fd } : null
    })
    .filter(Boolean)
})

const searchResultsCount = computed(() => {
  if (!searchQuery.value.trim()) return 0
  return filteredTimeline.value.reduce((s, m) => s + m.days.length, 0)
})

watch(searchQuery, () => {
  if (searchQuery.value.trim()) {
    filteredTimeline.value.forEach((m) => {
      expandedMonths.value[m.key] = true
    })
  }
})

function toggleMonth(key) {
  expandedMonths.value[key] = !expandedMonths.value[key]
}

function collapseAll() {
  for (const key of Object.keys(expandedMonths.value)) {
    expandedMonths.value[key] = false
  }
}

function expandAll() {
  for (const m of timeline.value) {
    expandedMonths.value[m.key] = true
  }
}

function selectDate(date) {
  selectedDate.value = date
}

function clearSearch() {
  searchQuery.value = ''
}

// Called after fetchAvailableDates — sets dates and auto-selects latest
function setAvailableDates(dates, analysisDatesList) {
  _availableDates.value = dates || []
  _datesWithAnalysis.value = new Set(analysisDatesList || [])
  if (dates && dates.length > 0) {
    const sorted = [...dates].sort().reverse()
    selectedDate.value = sorted[0]
    // Expand all months since only data dates are shown
    for (const dateStr of sorted) {
      const monthKey = dateStr.substring(0, 7)
      expandedMonths.value[monthKey] = true
    }
  }
}

const headerDateText = computed(() => {
  if (!selectedDate.value) return 'Memuat data...'
  return `${selectedDate.value} • Uprint.id`
})

export function useTimeline() {
  return {
    selectedDate,
    expandedMonths,
    searchQuery,
    filteredTimeline,
    searchResultsCount,
    headerDateText,
    toggleMonth,
    collapseAll,
    expandAll,
    selectDate,
    clearSearch,
    setAvailableDates,
  }
}
